import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { Check } from 'lucide-react';

import {
  AGENT_THINKING_CHROME_COLOR,
  AgentThinkingChromeText,
  AgentThinkingOrb,
} from '@/components/core';
import { AgentThinkingStep } from '@/types/chat';

type AgentThinkingTraceProps = {
  steps: AgentThinkingStep[];
  isLive: boolean;
};

const formatDuration = (step: AgentThinkingStep) => {
  if (step.state !== 'done' || step.endedAt == null) return null;
  const ms = Math.max(0, step.endedAt - step.startedAt);
  return `${(ms / 1000).toFixed(1)}s`;
};

const summarizeTrace = (steps: AgentThinkingStep[]) => {
  const first = steps[0];
  const last = steps[steps.length - 1];
  const endedAt = last.endedAt ?? last.startedAt;
  const seconds = Math.max(0, (endedAt - first.startedAt) / 1000);
  const toolCount = steps.filter(step => step.kind === 'tool').length;
  const duration = `${seconds.toFixed(1)}s`;

  if (toolCount === 0) return duration;
  return `${duration} · ${toolCount} tool${toolCount === 1 ? '' : 's'}`;
};

const StepRow = ({ step }: { step: AgentThinkingStep }) => {
  const duration = formatDuration(step);
  const isRunning = step.state === 'running';

  return (
    <HStack
      spacing={2}
      align='center'
      minH='20px'
      data-cy='agent-thinking-step'
      data-step-label={step.label}
    >
      {isRunning ? (
        <AgentThinkingOrb
          size='inline'
          status={step.label}
          toolName={step.toolName}
        />
      ) : (
        <Box
          boxSize='20px'
          display='flex'
          alignItems='center'
          justifyContent='center'
          color={AGENT_THINKING_CHROME_COLOR}
          flexShrink={0}
        >
          <Check size={12} />
        </Box>
      )}
      <AgentThinkingChromeText isLive={isRunning} noOfLines={1}>
        {step.label}
      </AgentThinkingChromeText>
      {duration ? (
        <Text
          fontSize='xs'
          color={AGENT_THINKING_CHROME_COLOR}
          flexShrink={0}
        >
          {duration}
        </Text>
      ) : null}
    </HStack>
  );
};

export const AgentThinkingTrace = ({
  steps,
  isLive,
}: AgentThinkingTraceProps) => {
  if (!steps.length) return null;

  return (
    <Box px={2} data-cy='agent-thinking-trace'>
      {isLive ? (
        <Stack gap={1}>
          {steps.map(step => (
            <StepRow key={step.id} step={step} />
          ))}
        </Stack>
      ) : (
        <Text fontSize='xs' color={AGENT_THINKING_CHROME_COLOR}>
          {summarizeTrace(steps)}
        </Text>
      )}
    </Box>
  );
};
